import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * PageTitle and PageHeader, the top of every desktop page.
 *
 * Source selectors: `system.css` `.dsk-page-title` (and the equivalent
 * `.dsk-page-header h1` descendant rule), `.dsk-page-header`,
 * `.dsk-page-header__sub`.
 *
 * Doctrine enforced:
 * - One `<h1>` per page, at `--dsk-display-sm` (28px), which is the top of the
 *   desktop 28 to 32 band. Everything below it is a `SectionTitle` at 20px.
 * - The header is `align-items: flex-end`, so the trailing actions sit on the
 *   title's baseline rather than floating at the top of a two-line block.
 * - `sub` is a `<p>` because `.dsk-page-header__sub` sets a top margin that a
 *   `<span>` would not honour.
 */
export const PageTitle = React.forwardRef(function PageTitle(
  { as: Component = 'h1', className, ...rest },
  ref,
) {
  return <Component ref={ref} className={cn('dsk-page-title', className)} {...rest} />;
});
PageTitle.displayName = 'PageTitle';

/**
 * PageHeader, `.dsk-page-header`. Title and optional sub on the left, the one
 * page-level command on the right.
 *
 *   <PageHeader title="My listings" sub="42 active" actions={<Button>New listing</Button>} />
 */
export const PageHeader = React.forwardRef(function PageHeader(
  { title, sub, actions, titleAs, children, className, ...rest },
  ref,
) {
  return (
    <header ref={ref} className={cn('dsk-page-header', className)} {...rest}>
      <div>
        {title ? <PageTitle as={titleAs}>{title}</PageTitle> : null}
        {sub ? <p className="dsk-page-header__sub">{sub}</p> : null}
        {children}
      </div>
      {/* CLASSED so the slot can hold more than one control. This was a bare
          `<div>`, which is enough for the single command the docstring above
          describes -- it is a flex ITEM of `.dsk-page-header` and one child
          needs no layout. A second control had nothing separating it: both
          `.dsk-btn` and `.dsk-segmented` are `inline-flex`, and React inserts
          no whitespace between JSX siblings, so the seller dashboard's
          Buyer/Seller toggle and its "Add new listing" button touched, and
          baseline-aligned at two different heights (40px against 38px). The
          rule is in `app-layer.css`, not `system.css`, because that file is a
          verbatim copy `sync.sh` overwrites. */}
      {actions ? <div className="dsa-pagehead__actions">{actions}</div> : null}
    </header>
  );
});
PageHeader.displayName = 'PageHeader';

export default PageHeader;
